import { useState, useCallback, useRef } from 'react'

export type AutoSaveStatus = 'idle' | 'saving' | 'error'

interface UseAutoSaveOptions {
  onSave: () => Promise<void>
  debounceMs?: number
}

interface UseAutoSaveResult {
  saveStatus: AutoSaveStatus
  triggerSave: () => void
  retryFailedSave: () => void
}

/**
 * Auto-save hook with debouncing and error handling
 *
 * This is a simpler version that just tracks save status without
 * managing the data itself. The caller is responsible for passing
 * data to the save function.
 *
 * Features:
 * - Debounced saves to avoid excessive API calls
 * - Tracks saving/idle/error states
 * - Retry mechanism for failed saves
 */
export function useAutoSave({
  onSave,
  debounceMs = 1000
}: UseAutoSaveOptions): UseAutoSaveResult {
  const [saveStatus, setSaveStatus] = useState<AutoSaveStatus>('idle')
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const onSaveRef = useRef(onSave)

  // Keep onSave ref updated to avoid stale closures
  onSaveRef.current = onSave

  // Perform the actual save
  const performSave = useCallback(async () => {
    setSaveStatus('saving')
    try {
      await onSaveRef.current()
      setSaveStatus('idle')
    } catch (error) {
      console.error('Auto-save failed:', error)
      setSaveStatus('error')
    }
  }, [])

  // Trigger a debounced save
  const triggerSave = useCallback(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    // Set new debounced save
    timeoutRef.current = setTimeout(() => {
      performSave()
    }, debounceMs)
  }, [debounceMs, performSave])

  // Retry failed save immediately
  const retryFailedSave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    performSave()
  }, [performSave])

  return { saveStatus, triggerSave, retryFailedSave }
}

export default useAutoSave
